// @ts-strict-ignore
import React, { useEffect, useRef, useState } from 'react'
import { Zoom } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import classNames from 'classnames'
import { MediaItem } from 'mytours-read-api-typescript'
import { CollectionItem, ScreenSectionItem } from 'mytours-read-common'
import { getDirMode } from '../../../platform'
import { mergeRefs } from 'mobile_web/modules/common/functions'
import { Style } from '../../../../types'
import strings from '../../../../strings'

import 'swiper/css/bundle'
import '../styles/slider.less'
import { Button } from '@mytours/components'

type DataType = ScreenSectionItem | MediaItem | CollectionItem

interface SlideOptions {
	autoHeight?: boolean
	nested?: boolean
	shortSwipes?: boolean
	freeMode?: boolean
	slidesPerView?: 'auto' | number
	spaceBetween?: number
	breakpoints?: Record<string | number, { sliderPerView?: number; spaceBetween?: number }>
	watchOverflow?: boolean
}

interface Props {
	data: DataType[]
	styles?: Style
	className?: string
	slideClassName?: string
	sliderClassName?: string
	initialSlide?: number
	slideOptions?: SlideOptions
	renderItem: (section: DataType, index?: number, isActive?: boolean) => JSX.Element
	getActiveSliderIndex?: (index: number) => void
	getRef?: (ref: SwiperClass) => void
	onSlideChange?: () => void
	onSlideClick?: (index: number) => void
	enableControls?: boolean
	controlsClassName?: string
	swiperWrapperElement?: (element: HTMLDivElement) => void
	getControlsRef?: (element: HTMLDivElement) => void
	loop?: boolean
}

export type SwiperClass = typeof Swiper & {
	slideNext: () => void
	slidePrev: () => void
	update: () => void
	activeIndex: number
	isBeginning: boolean
	isEnd: boolean
	getSwiper: () => any
	realIndex: number
	enabled: boolean
}

const Slider: React.ForwardRefRenderFunction<HTMLDivElement, Props> = (
	{
		initialSlide,
		data,
		onSlideClick,
		slideClassName,
		onSlideChange: onSlideChangeProp,
		getActiveSliderIndex,
		renderItem,
		enableControls = false,
		controlsClassName,
		slideOptions,
		sliderClassName,
		styles,
		className,
		swiperWrapperElement,
		getRef,
		getControlsRef,
		loop,
	},
	ref,
) => {
	const [isBeginning, setIsBeginning] = useState<boolean>(initialSlide === 0 && !loop)
	const [isEnd, setIsEnd] = useState<boolean>(initialSlide === data.length - 1 && !loop)
	const [swiperClass, setSwiper] = useState<SwiperClass>()
	let findWrapperInterval = null

	const slidesRef = useRef<HTMLDivElement>()

	const slideOptionsInitial = {
		speed: 300,
		slidesPerView: 1,
		runCallbacksOnInit: true,
		updateOnWindowResize: true,
		shortSwipes: false,
		longSwipesRatio: 0.1,
		initialSlide,
	}

	useEffect(
		() => () => {
			if (findWrapperInterval) {
				clearInterval(findWrapperInterval)
			}
		},
		[],
	)

	useEffect(() => {
		// The next interval does fix for Swiper.js module
		// During first load swiper-wrapper doesn't contain transform styles
		// So, we need to do force swiper update
		findWrapperInterval = setInterval(() => {
			const elements = slidesRef?.current?.querySelectorAll('.swiper .swiper-wrapper')
			if ((elements?.[0] as HTMLElement)?.style?.transform || (elements?.[0] as HTMLElement)?.style?.webkitTransform) {
				swiperWrapperElement?.(elements?.[0] as HTMLDivElement)
				clearInterval(findWrapperInterval)
			} else if (elements?.[0]) {
				swiperClass?.update()
			}
		}, 10)

		getRef?.(swiperClass)
	}, [data, swiperClass])

	const swiperSlides = (): JSX.Element[] | JSX.Element =>
		data?.map((section: DataType, index: number) => (
			<SwiperSlide
				key={index}
				className={classNames('slide', slideClassName, section.media_type || '')}
				onClick={onSlideClick ? () => onSlideClick(index) : undefined}
				zoom={true}
			>
				{renderItem(section, index)}
			</SwiperSlide>
		))

	const onSlideChange = () => {
		onSlideChangeProp?.()

		if (swiperClass) {
			const activeSlideIndex = swiperClass.realIndex

			if (getActiveSliderIndex) {
				getActiveSliderIndex(activeSlideIndex)
			}

			const nextIsBeginning = swiperClass.isBeginning && !loop
			const nextIsEnd = swiperClass.isEnd && !loop

			if (isBeginning !== nextIsBeginning || isEnd !== nextIsEnd) {
				setIsBeginning(nextIsBeginning)
				setIsEnd(nextIsEnd)
			}
		}
	}

	// A swiper whose slides all fit reports its edges at init and on resize, without ever changing slide.
	const syncEdges = (swiper: { isBeginning: boolean; isEnd: boolean }) => {
		setIsBeginning(swiper.isBeginning && !loop)
		setIsEnd(swiper.isEnd && !loop)
	}

	const slidePrev = (): void => {
		swiperClass.slidePrev()
	}

	const slideNext = (): void => {
		swiperClass.slideNext()
	}

	const renderControls = (): React.ReactNode => {
		if (!enableControls) {
			return null
		}

		return (
			<div ref={getControlsRef} className={classNames('slider-controls', controlsClassName)}>
				<Button
					variant="clear"
					className={classNames('control control-prev media-action-click', { visible: !isBeginning || loop })}
					onClick={slidePrev}
					aria-label={strings.accessibility_previous}
					data-cy="slider-prev-button"
				>
					<i className="fa fa-chevron-left" aria-hidden="true" />
				</Button>
				<Button
					variant="clear"
					className={classNames('control control-next media-action-click', { visible: !isEnd || loop })}
					onClick={slideNext}
					aria-label={strings.accessibility_next}
					data-cy="slider-next-button"
				>
					<i className="fa fa-chevron-right" aria-hidden="true" />
				</Button>
			</div>
		)
	}

	const onSwiper = (swiperClass: any) => {
		setSwiper(swiperClass as SwiperClass)
		syncEdges(swiperClass)
	}

	if (!data || data?.length === 0) {
		return null
	}

	return (
		<div className={classNames('slider-wrapper', className)} dir={getDirMode()} ref={mergeRefs(slidesRef, ref)}>
			<Swiper
				modules={[Zoom]}
				dir={getDirMode()}
				className={classNames('slider', slideOptions?.slidesPerView === 'auto' && 'slides-per-view', sliderClassName)}
				slidesPerView={3}
				onSlideChange={onSlideChange}
				onUpdate={syncEdges}
				onResize={syncEdges}
				onSwiper={onSwiper}
				style={styles || {}}
				loop={!!loop}
				loopAdditionalSlides={0}
				{...slideOptionsInitial}
				{...slideOptions}
				zoom={true}
			>
				{swiperSlides()}
			</Swiper>
			{renderControls()}
		</div>
	)
}

export default React.forwardRef(Slider)
